import {
  BrandIcon,
  EditStepIcon,
  IconSizeEnum,
  RelevantPeopleIcon,
  ScreenTextOcrIcon,
  STTIcon,
  VideoCutsCortesIcon,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckboxForm } from '@/components/FormFields/CheckboxForm';

export type TEnrichmentsForm = {
  ocr: boolean;
  stt: boolean;
  logos: boolean;
  peoples: boolean;
  advertising: boolean;
  overwrite_detections: boolean;
};

type EnrichmentsSelectorProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  allowAdvertising?: boolean;
  isRadio?: boolean;
};

export function EnrichmentsSelector<T extends FieldValues>({
  form,
  allowAdvertising = true,
  isRadio = false,
}: EnrichmentsSelectorProps<T>) {
  const { t } = useTranslation();

  const isLogosOrPeoplesSelected =
    form.watch('logos' as Path<T>) || form.watch('peoples' as Path<T>);

  useEffect(() => {
    if (!isLogosOrPeoplesSelected) {
      form.setValue('overwrite_detections' as Path<T>, false as unknown as T[keyof T]);
    }
  }, [isLogosOrPeoplesSelected]);

  useEffect(() => {
    if (isRadio) {
      form.setValue('ocr' as Path<T>, false as unknown as T[keyof T]);
      form.setValue('logos' as Path<T>, false as unknown as T[keyof T]);
      form.setValue('peoples' as Path<T>, false as unknown as T[keyof T]);
    }
  }, [isRadio, form]);

  return (
    <div className="flex w-full flex-col gap-3">
      <CheckboxForm
        form={form}
        name={'ocr' as Path<T>}
        label={t('Texto en pantalla (OCR)')}
        icon={<ScreenTextOcrIcon size={IconSizeEnum.MD} />}
        disabled={isRadio}
      />

      <hr />

      <CheckboxForm
        form={form}
        name={'stt' as Path<T>}
        label={t('Activar transcripción de texto')}
        icon={<STTIcon size={IconSizeEnum.MD} />}
        mapChange={(onChange, event) => {
          if (event.currentTarget.value === 'false') {
            form.setValue('advertising' as Path<T>, false as unknown as T[keyof T]);
          }
          onChange(event);
        }}
      />

      <hr />

      <CheckboxForm
        form={form}
        name={'logos' as Path<T>}
        label={t('Localizar logos')}
        icon={<BrandIcon size={IconSizeEnum.MD} />}
        disabled={isRadio}
      />

      <hr />

      <CheckboxForm
        form={form}
        name={'peoples' as Path<T>}
        label={t('Personas de interés')}
        icon={<RelevantPeopleIcon size={IconSizeEnum.MD} />}
        disabled={isRadio}
      />

      {allowAdvertising && (
        <>
          <hr />
          <CheckboxForm
            form={form}
            name={'advertising' as Path<T>}
            label={t('Cortes automáticos')}
            icon={<VideoCutsCortesIcon size={IconSizeEnum.MD} />}
            mapChange={(onChange, event) => {
              if (event.currentTarget.value === 'true') {
                form.setValue('stt' as Path<T>, true as unknown as T[keyof T]);
              }
              onChange(event);
            }}
          />
        </>
      )}
      <hr />
      <CheckboxForm
        form={form}
        name={'overwrite_detections' as Path<T>}
        label={t('Sobreescribir detecciones de IA (Logos - Personas)')}
        icon={<EditStepIcon size={IconSizeEnum.MD} />}
        disabled={!isLogosOrPeoplesSelected}
      />
    </div>
  );
}
